import type { MarkSpec, NodeSpec } from 'prosemirror-model';
import { Schema } from 'prosemirror-model';
import type { Observable } from 'rxjs';

import type { Placeholder } from '@core/wysiwyg-legacy';

import type { RtSchemaType } from '../editor/types';
import {
  DocNode,
  HardBreakNode,
  ParagraphNode,
  RichDocNode,
  TextNode,
} from '../plugins/basic/nodes';
import { BlockquoteNode } from '../plugins/blockquote/blockquote-node';
import { ChecklistItemNode } from '../plugins/checklist/nodes/checklist-item.node';
import { ChecklistNode } from '../plugins/checklist/nodes/checklist.node';
import { ColorMark } from '../plugins/color/color-mark';
import { ColumnNode } from '../plugins/columns/nodes/column.node';
import { ColumnsNode } from '../plugins/columns/nodes/columns.node';
import { HeadingNode } from '../plugins/heading/heading-node';
import { HighlightMark } from '../plugins/highlight/highlight-mark';
import { ImageNode } from '../plugins/image/image-node';
import { BulletListNode } from '../plugins/list/nodes/bullet-list.node';
import { ListItemNode } from '../plugins/list/nodes/list-item.node';
import { OrderedListNode } from '../plugins/list/nodes/ordered-list.node';
import { StrongMark } from '../plugins/marks/bold/bold-mark';
import { EmMark } from '../plugins/marks/italic/italic-mark';
import { LinkMark } from '../plugins/marks/link/link-mark';
import { StrikethroughMark } from '../plugins/marks/strikethrough/strikethrough-mark';
import { UnderlineMark } from '../plugins/marks/underline/underline-mark';
import { LegacyMentionNode, MentionNode } from '../plugins/mentions/nodes';
import type { MentionElement, MentionElementGroup } from '../plugins/mentions/types';
import { PlaceholderNode } from '../plugins/placeholders/nodes';
import { LinkNode } from '../plugins/scheduler-links/nodes';
import { SizeMark } from '../plugins/size/size-mark';
import { VideoNode } from '../plugins/video/nodes/video-node';

export interface DefaultSchemaOptions {
  mentionDataSource$?: Observable<MentionElement[] | MentionElementGroup[]>;
  placeholdersDataSource$?: Observable<Placeholder[]>;
  checklistViewOnly?: boolean;
  mentionViewOnly?: boolean;
}

export type ProsemirrorAttrs<T> = Record<keyof T, { default?: string | null | boolean }>;

export enum RtSchema {
  Default = 'default',
  Basic = 'basic',
  Texting = 'texting',
  JobHighlight = 'job_highlight',
  JobDescription = 'job_description',
  JobRequirements = 'job_requirements',
  TalentPoolDescription = 'talent_pool_description',
  OfferLetterDescription = 'offer_letter_description',
  Rich = 'rich',
}

// prettier-ignore
export type DefaultSchema = RtSchemaType<
  typeof DocNode | typeof TextNode | typeof ParagraphNode | typeof HardBreakNode | typeof ChecklistNode | typeof ChecklistItemNode | typeof OrderedListNode | typeof BulletListNode | typeof ListItemNode | typeof MentionNode | typeof LegacyMentionNode,
  typeof StrongMark | typeof EmMark | typeof StrikethroughMark | typeof UnderlineMark | typeof LinkMark
>;

// prettier-ignore
export type RichSchema = RtSchemaType<
  typeof DocNode | typeof TextNode | typeof ParagraphNode | typeof HeadingNode | typeof HardBreakNode | typeof ChecklistNode | typeof ChecklistItemNode | typeof OrderedListNode | typeof BulletListNode | typeof ListItemNode | typeof MentionNode | typeof LegacyMentionNode | typeof BlockquoteNode | typeof ImageNode | typeof VideoNode,
  typeof StrongMark | typeof EmMark | typeof StrikethroughMark | typeof UnderlineMark | typeof LinkMark | typeof ColorMark | typeof HighlightMark | typeof SizeMark
>;

export const defaultSchemaFactory = (
  document: Document,
  options?: DefaultSchemaOptions,
): DefaultSchema =>
  buildSchema({
    nodes: [
      new DocNode(),
      new TextNode(),
      new ParagraphNode(),
      new HardBreakNode(),
      new ChecklistNode(options?.checklistViewOnly ?? false),
      new ChecklistItemNode(document, options?.checklistViewOnly ?? false),
      new OrderedListNode(),
      new BulletListNode(),
      new ListItemNode(),
      new MentionNode(options?.mentionViewOnly ?? false, options?.mentionDataSource$),
      new LegacyMentionNode(options?.mentionViewOnly ?? false),
      new PlaceholderNode(),
      new LinkNode(),
    ],
    marks: [
      new StrongMark(),
      new EmMark(),
      new StrikethroughMark(),
      new UnderlineMark(),
      new LinkMark(),
    ],
  });

export const richSchemaFactory = (document: Document, options?: DefaultSchemaOptions): RichSchema =>
  buildSchema({
    nodes: [
      new RichDocNode(),
      new TextNode(),
      new ParagraphNode({ withAlign: true, withHtmlClass: true }),
      new HeadingNode({ withAlign: true, withHtmlClass: true }),
      new BlockquoteNode({ withHtmlClass: true }),
      new HardBreakNode(),
      new OrderedListNode({ withHtmlClass: true }),
      new BulletListNode({ withHtmlClass: true }),
      new ListItemNode(),
      new LinkNode(),
      new ImageNode({ withAlign: true, withHtmlClass: true }),
      new VideoNode({ withAlign: true, withHtmlClass: true }),
      new ColumnsNode({ withHtmlClass: true }),
      new ColumnNode(),
    ],
    marks: [
      new StrongMark(),
      new EmMark(),
      new StrikethroughMark(),
      new UnderlineMark(),
      new LinkMark(),
      new SizeMark(),
      new ColorMark(),
      new HighlightMark(),
    ],
  });

export const basicSchemaFactory = (document: Document): DefaultSchema =>
  buildSchema({
    nodes: [new DocNode(), new TextNode(), new ParagraphNode(), new HardBreakNode()],
    marks: [new StrongMark(), new EmMark(), new UnderlineMark(), new LinkMark()],
  });

export const textingSchemaFactory = (document: Document): DefaultSchema =>
  buildSchema({
    nodes: [
      new DocNode(),
      new TextNode(),
      new ParagraphNode(),
      new HardBreakNode(),
      new LinkNode(),
    ],
    marks: [],
  });

export const jobHighlightSchemaFactory = (document: Document): DefaultSchema =>
  buildSchema({
    nodes: [new DocNode(), new TextNode(), new ParagraphNode(), new HardBreakNode()],
    marks: [new StrongMark(), new EmMark(), new UnderlineMark()],
  });

const buildSchema = ({ nodes, marks }: { nodes: NodeSpec[]; marks: MarkSpec[] }) => {
  const nodesSpec = Object.fromEntries(nodes.map(node => [(<any>node).constructor.nodeName, node]));
  const marksSpec = Object.fromEntries(marks.map(mark => [(<any>mark).constructor.markName, mark]));

  return new Schema({ nodes: nodesSpec, marks: marksSpec });
};
